import {EventBus} from "../../../../common/ts/events/EventBus";
import SessionStorageService from "../../../../common/ts/services/SessionStorageService";
import PageGallery from "../../../../common/ts/components/PageGallery";
import LazyLoadingComponent from "../../../../common/ts/components/LazyLoadingComponent";
import showModalSimilars from "../../serp/showModalSimilars";
import {WhitelabelEvent, WhitelabelEventWithPayload} from "../../../../common/ts/events/Events";
import tracking from "../../../../common/ts/core/tracking";
import {
    SECTION_CLICKED_EVENT_TYPE
} from "../../../../common/ts/tracking/constants";
import {Section} from "../../../../common/ts/services/Section";

interface SnippetData {
    pageViewId: string;
    search: string;
    getSimilarsEvent: (params: RetrievalEventParams) => void;
    eventBus: EventBus;
    findIntent?: FindIntent;
}

export class Snippet {
    pageViewId: string;
    search: string;
    getSimilarsEvent: (params: RetrievalEventParams) => void;
    eventBus: EventBus;
    findIntent: FindIntent;

    adsClicked: string[] = [];

    constructor({pageViewId, search, getSimilarsEvent, eventBus, findIntent = {}}: SnippetData) {
        this.pageViewId = pageViewId;
        this.search = search;
        this.getSimilarsEvent = getSimilarsEvent;
        this.eventBus = eventBus;
        this.findIntent = findIntent;
    }

    public init() {
        this.eventBus.subscribe(SimilarAdsReceivedEvent.TYPE, this.similarAdsReceivedEventHandler());

        const snippets = Array.from(document.querySelectorAll('.listing-card')) as HTMLElement[];
        snippets.forEach((item) => this.registerSnippetEvents(item))
    }

    private registerSnippetEvents(snippet: HTMLElement) {
        if (this.hasViewedTagSupport(snippet)) {
            this.checkIfSnippetHasBeenViewed(snippet);
        }

        snippet.addEventListener('click', (e) => {
            const shouldShowModalSimilars = snippet.dataset.shouldshowmodalsimilars === "true";
            const shouldShowClickSimilars = snippet.dataset.shouldshowclicksimilars === "true";
            const sectionId = (document.querySelector('#listings') as HTMLElement | null)?.dataset.tracking;

            const customEventOrigin = e.target ? (e.target as HTMLElement).dataset.eventOrigin : null;
            const hasValidCustomEventOriginForSimilars = !customEventOrigin || !["info-button", "back-button"].includes(customEventOrigin);

            if (sectionId && hasValidCustomEventOriginForSimilars) {
                if (shouldShowClickSimilars) {
                    this.showClickSimilars(e, sectionId);
                }

                if (shouldShowModalSimilars) {
                    showModalSimilars(snippet.dataset.listingid, this.pageViewId, sectionId, this.findIntent);
                }
            }

            this.handleCustomClickOrigin(e, snippet);
        });
    }

    private showClickSimilars(event: Event, sectionId: string) {
        if (event.target) {
            const clickedElement = event.target as HTMLElement | null;
            if (clickedElement) {
                const eventOrigin = clickedElement.dataset.eventOrigin;
                const notValidSimilarClickEventOrigins = ["info-button", "back-button"];
                if (!eventOrigin || !notValidSimilarClickEventOrigins.includes(eventOrigin)) {
                    const clickedListing = clickedElement.closest(".listing-card") as HTMLElement | null;
                    if (clickedListing) {
                        const clickedListingId = clickedListing.dataset.listingid;
                        if (clickedListingId && !this.adsClicked.includes(clickedListingId)) {
                            const ids = Array.from(document.querySelectorAll('[data-listingId]')).map(e => e.getAttribute('data-listingId'));
                            this.getSimilarsEvent({
                                adId: clickedListingId,
                                clickedListing: clickedListing,
                                numberOfAds: 3,
                                search: this.findIntent.currentSearch,
                                ids: ids,
                                pageSectionId: sectionId,
                                successEventClass: SimilarAdsReceivedEvent,
                                pageViewId: this.pageViewId,
                                retrievalEvent: this.getSimilarsEvent,
                            })
                        }
                    }
                }
            }
        }
    }

    private trackViewMorePhotosEvent() {
        EventBus.getInstance().emit(new tracking.TrackWebTrackingEvent({
            type: SECTION_CLICKED_EVENT_TYPE,
            serpUrl: window.location.href,
            sectionName: Section.SECTION_SNIPPET_VIEW_PHOTO,
            section: "7002",
            pageViewId: this.pageViewId,
        }));
    }

    private trackContactInWebEvent() {
        EventBus.getInstance().emit(new tracking.TrackWebTrackingEvent({
            type: SECTION_CLICKED_EVENT_TYPE,
            serpUrl: window.location.href,
            sectionName: Section.SECTION_SNIPPET_CONTACT_IN_WEB,
            section: "7004",
            pageViewId: this.pageViewId,
        }));
    }

    private trackViewDetailEvent() {
        EventBus.getInstance().emit(new tracking.TrackWebTrackingEvent({
            type: SECTION_CLICKED_EVENT_TYPE,
            serpUrl: window.location.href,
            sectionName: Section.SECTION_SNIPPET_VIEW_DETAIL,
            section: "7003",
            pageViewId: this.pageViewId,
        }));
    }

    private similarAdsReceivedEventHandler() {
        return (event: WhitelabelEvent) => {
            const {
                payload: {
                    response,
                    clickedListing
                }
            } = (event as SimilarAdsReceivedEvent)

            this.renderSimilarAds(response, clickedListing)
        }
    }

    private renderSimilarAds(html: string, clickedListing: HTMLElement) {
        const elements = new DOMParser().parseFromString(html, 'text/html')
        const clickedListingId = clickedListing.dataset.listingid;

        if (clickedListingId) {
            Array.from(elements.body.children).forEach(element => {
                const htmlElement = element as HTMLElement
                htmlElement.dataset.similarClickAd = clickedListingId
            })

            let similarAds = document.querySelectorAll(`[data-similar-click-ad="` + clickedListingId + `"]`);

            if (similarAds.length == 0) {
                clickedListing.insertAdjacentHTML('afterend', elements.body.innerHTML);
                similarAds = document.querySelectorAll(`[data-similar-click-ad="` + clickedListingId + `"]`);
            }

            new LazyLoadingComponent().checkLazyLoad();
            new PageGallery(".swiper-container-similar-" + clickedListingId);

            this.adsClicked.push(clickedListingId);

            similarAds.forEach(snippet => {
                const similarSnippet = snippet as HTMLElement;

                this.adsClicked.push(similarSnippet.dataset.listingid!.toString())

                if (this.hasViewedTagSupport(similarSnippet)) {
                    this.checkIfSnippetHasBeenViewed(similarSnippet);
                }

                this.registerSnippetEvents(similarSnippet);
            })
        }
    }

    private handleCustomClickOrigin(event: MouseEvent, snippet: HTMLElement) {
        const swiperArrowClicked = event.target as HTMLElement
        if (swiperArrowClicked?.classList.contains('swiper-button-adform')) return;

        const customEventOrigin = event.target ? (event.target as HTMLElement).dataset.eventOrigin : null;
        if (customEventOrigin === "info-button") {
            event.stopPropagation();
            event.preventDefault();

            this.showListingDescription(snippet);
        } else if (customEventOrigin === "back-button") {
            event.stopPropagation();
            event.preventDefault();

            this.hideListingDescription(snippet);
        } else if (customEventOrigin === "view_photo_button") {
            this.trackViewMorePhotosEvent();
            this.goToListingUrl(snippet);
        } else if (customEventOrigin === "view_detail_button") {
            this.trackViewDetailEvent();

            if (snippet.dataset.shouldcustomredirect === "true") {
                this.handleViewDetailButtonClick(event, snippet);
            }
        } else if (customEventOrigin === "contact_in_web_button") {
            this.trackContactInWebEvent();
        } else {
            this.goToListingUrl(snippet);
        }

        const hasValidCustomEventOriginForMarkAsViewed = !customEventOrigin ||
            !["info-button", "back-button"].includes(customEventOrigin);

        if (hasValidCustomEventOriginForMarkAsViewed && this.hasViewedTagSupport(snippet)) {
            this.markSnippetAsViewed(snippet);
        }
    }

    private goToListingUrl(snippet: HTMLElement) {
        const snippetUrl = snippet.dataset.snippeturl;
        const shouldOpenInNewTab = snippet.dataset.shouldopeninnewtab;
        const target = shouldOpenInNewTab === "true" ? "_blank" : "_self";
        if (snippetUrl) window.open(snippetUrl, target);
    }

    private markSnippetAsViewed(snippet: HTMLElement) {
        const localStorageService = new SessionStorageService();
        const listingId = snippet.dataset.listingid;
        if (
            listingId &&
            !this.hasBeenVisited(listingId)
        ) {
            localStorageService.saveArray("clickedIds", [listingId]);
            this.showViewedLabel(snippet);
            this.markTitleAsViewed(snippet);
        }
    }

    private showListingDescription(snippet: HTMLElement) {
        const informationBlock = snippet.querySelector('div[data-content]') as HTMLElement | null;
        const informationBlockHeight = informationBlock?.clientHeight;
        const descriptionBlock = snippet.querySelector('div[data-description]') as HTMLElement | null;
        const infoButton = snippet.querySelector('div[data-infobutton]') as HTMLElement | null;
        const backButton = snippet.querySelector('div[data-backbutton]') as HTMLElement | null;

        if (infoButton) {
            infoButton.style.display = 'none';
        }

        if (backButton) {
            backButton.style.display = 'block';
        }

        if (informationBlock) {
            informationBlock.style.display = 'none';
        }

        if (descriptionBlock) {
            descriptionBlock.style.display = '-webkit-box';
            descriptionBlock.style.maxHeight = `${informationBlockHeight}px`
            descriptionBlock.style.minHeight = `calc(${informationBlockHeight}px - 4px)`
        }
    }

    private hasViewedTagSupport(snippet: HTMLElement) {
        return snippet.querySelector(".listing-card__title-and-extra-info__extra-info__viewed") != null
    }

    private hideListingDescription(snippet: HTMLElement) {
        const informationBlock = snippet.querySelector('div[data-content]') as HTMLElement | null;
        const descriptionBlock = snippet.querySelector('div[data-description]') as HTMLElement | null;
        const infoButton = snippet.querySelector('div[data-infobutton]') as HTMLElement | null;
        const backButton = snippet.querySelector('div[data-backbutton]') as HTMLElement | null;

        if (infoButton) {
            infoButton.style.display = 'block';
        }

        if (backButton) {
            backButton.style.display = 'none';
        }

        if (informationBlock) {
            informationBlock.style.display = 'flex';
        }

        if (descriptionBlock) {
            descriptionBlock.style.display = 'none';
        }
    }

    private checkIfSnippetHasBeenViewed(snippet: HTMLElement) {
        const listingId = snippet.dataset.listingid;
        if (listingId && this.hasBeenVisited(listingId)) {
            this.showViewedLabel(snippet);
            this.markTitleAsViewed(snippet);
        }
    }

    private hasBeenVisited(listingId: string) {
        const localStorageService = new SessionStorageService();
        return listingId && localStorageService.valueExistsInArray("clickedIds", listingId);
    }

    private showViewedLabel(snippet: HTMLElement) {
        const viewedTag = snippet.querySelector(".listing-card__title-and-extra-info__extra-info__viewed") as HTMLElement | null;
        if (viewedTag) {
            viewedTag.style.display = "";
        }
    }

    private markTitleAsViewed(snippet: HTMLElement) {
        snippet.querySelector(".title")?.classList.add("highlighted")
    }

    private handleViewDetailButtonClick(event: MouseEvent, snippet: HTMLElement) {
        const hasCampaigns = snippet.dataset.hascampaigns === "true";
        const isLeadgen = snippet.dataset.isleadgen === "true";

        if (hasCampaigns && !isLeadgen) {
            event.preventDefault();
            event.stopPropagation();

            this.goToDetailPage(event, snippet);
        }
    }

    private goToDetailPage(event: MouseEvent, snippet: HTMLElement): void {
        const listingId = snippet.dataset.listingid;

        if (listingId) {
            const href = (event.target as HTMLElement).getAttribute('href');
            if (href != null) {
                window.location.href = href;
            }
        }
    }
}

interface SimilarAdsReceivedPayload {
    response: string,
    clickedListing: HTMLElement,
    search: string,
    pageViewId: string,
    pageSectionId: string,
    retrievalEvent: (params: RetrievalEventParams) => void,
}

class SimilarAdsReceivedEvent implements WhitelabelEventWithPayload<SimilarAdsReceivedPayload> {
    type: string;
    static readonly TYPE: string = 'similar-ads-received';
    payload: SimilarAdsReceivedPayload;

    constructor(payload: SimilarAdsReceivedPayload) {
        this.type = SimilarAdsReceivedEvent.TYPE;
        this.payload = payload;
    }
}

export type FindIntent = {
    [key: string]: string
}

type RetrievalEventParams = {
    search?: string,
    pageViewId: string,
    pageSectionId: string,
    retrievalEvent: (params: RetrievalEventParams) => void,
    clickedListing: HTMLElement,
    adId: string,
    numberOfAds: number,
    ids: (string | null)[],
    successEventClass: any,
}
